import React, { useState, useMemo, useReducer } from 'react'
import { Form, Header, Divider, Icon, Button } from 'semantic-ui-react'
import InputsForm from './InputsForm'
import { useSubscription } from 'utils'
import { finalize, tap, map } from 'rxjs/operators'
import { forkJoin } from 'rxjs'
import request from 'services/http'
import { DateTime, Interval } from 'luxon'
import history from 'services/history'

const initialFormValues = {
    empresa: '',
    funcionario: '',
    periodo: '',
    observacao: ''
}

function formReducer(state, action) {
    if ('reset' in action) 
        return initialFormValues
    if (!('payload' in action))
        return state
    return { ...state, ...action.payload }
}

export default function DigitarEventos() {

    const [form, setForm] = useReducer(formReducer, initialFormValues)
    const [loading, setLoading] = useState(true)
    const [isFetchingSaved, setIsFetchingSaved] = useState(null)
    const [inputs, setInputs] = useState([])
    const [eventsOptions, setEventsOptions] = useState([])
    const [funcionariosFilter, setFuncionariosFilter] = useState([])
    const [dados, setDados] = useState({
        eventos: [],
        funcionarios: [],
        empresas: []
    })

    const period = Interval.fromDateTimes(DateTime.local().startOf('month'), DateTime.local().plus({ months: 1 })).splitBy({ months: 1})
    const periodoOptions = period.map(it => ({
        text: it.start.monthLong[0].toUpperCase() + it.start.monthLong.slice(1) + ' / ' + it.start.year,
        value: it.start.toFormat('yyyyMM')
    }))

    const inputs$ = useMemo(() => request({
        method: 'GET',
        url: '/departamento-pessoal/inputs'
    }).pipe(
        map(({ response }) => response.inputs)
    ), [])

    const empresas$ = useMemo(() => request({
        method: 'GET',
        url: '/departamento-pessoal/empresas'
    }).pipe(
        map(({ response }) => response.empresas)
    ), [])

    const funcionarios$ = useMemo(() => request({
        method: 'GET',
        url: '/departamento-pessoal/funcionarios'
    }).pipe(
        map(({ response }) => response.funcionarios)
    ), [])

    const fetchDados$ = useMemo(() => forkJoin({ 
        inputs: inputs$, 
        empresas: empresas$,
        funcionarios: funcionarios$
    }).pipe(
        tap(response => {
            setDados({
                eventos: response.inputs,
                empresas: response.empresas,
                funcionarios: response.funcionarios
            })
            setEventsOptions(response.inputs.filter((_, idx) => idx < 100).map(x => ({ key: x.id, text: `${x.id} - ${x.descricao}`, value: x.id })))
        }),
        finalize(() => setLoading(false))
    ), [setDados, inputs$, empresas$, funcionarios$])

    useSubscription(fetchDados$, loading)

    const fetchSavedEventos$ = useMemo(() => request({
        url: '/departamento-pessoal/eventos',
        method: 'GET',
        params: {
            ...form,
            ...isFetchingSaved
        }
    }).pipe(
        tap(({ response }) => {
            const eventos = response.eventos.map(it => {
                const label = it.consolidado ? it.id + ' - ' + it.descricao : (<><strong>{it.id + ' - ' + it.descricao}</strong> <Icon onClick={() => handleRemove(it.id)} name='times' size='large' color='red' style={{ float: 'right', marginRight: 0, cursor: 'pointer' }} /></>)
                return ({
                    label,
                    id: it.id_evento,
                    placeholder: it.complemento,
                    name: it.id_evento,
                    autoComplete: "off",
                    value: it.valor,
                    readOnly: it.consolidado ? true : false
                })
            })
            setInputs(eventos)
            if (response.observacao) 
                setForm({ payload: { observacao: response.observacao } })
        }),
        finalize(() => setIsFetchingSaved(null))
    ), [form, isFetchingSaved])
    useSubscription(fetchSavedEventos$, isFetchingSaved !== null)

    function handleChange(_, { name, value }) {
        switch (name) {
            case 'inputs':
                const evento = dados.eventos.find(it => it && it.id === value)
                if (!inputs.filter(it => it.id === evento.id).length) {
                    setInputs(state => {
                        const label = (<><strong>{evento.id + ' - ' + evento.descricao}</strong> <Icon onClick={() => handleRemove(evento.id)} name='times' size='large' color='red' style={{ float: 'right', marginRight: 0, cursor: 'pointer' }} /></>)
                        state.push({
                            label,
                            id: evento.id,
                            placeholder: evento.complemento,
                            name: evento.id,
                            autoComplete: "off"
                        })
                        return state
                    })
                }
                break
            case 'funcionario':
                setIsFetchingSaved({ funcionario: value })
                if (dados.funcionarios.find(it => it.id === value).id_empresa !== form.empresa) {
                    setForm({ payload: { empresa: dados.funcionarios.find(it => it.id === value).id_empresa } })
                    // Force blur behavior
                    handleEventsBlur(_, { name: 'funcionario' })
                }
                break
            case 'periodo':
                if (form.funcionario)
                    setIsFetchingSaved({ periodo: value })
                break
            default:
                break
        }

        setForm({ payload: { [name]: value } })
    }

    function handleRemove(id) {
        setInputs(state => {
            return state.filter(it => it.id !== id)
        })
    } 

    function searchEvents(_, { searchQuery, name }) {
        const regex = new RegExp(searchQuery, 'gi')
        const filtered = name === 'inputs' ? 
            dados.eventos.filter(it => `${it.id} - ${it.descricao}`.match(regex)) 
            : dados.funcionarios.filter(it => `${it.codigo} - ${it.nome} / ${it.cargo}`.match(regex))

        if (filtered.length) {
            if (name === 'inputs')
                setEventsOptions(filtered.map(it => ({ key: it.id, value: it.id, text: `${it.id} - ${it.descricao}` })))
            else 
                setFuncionariosFilter(filtered.map(it => ({ key: it.id, value: it.id, text: `${it.codigo} - ${it.nome} / ${it.cargo}`  })))
        } else {
            if (name === 'inputs')
                setEventsOptions(dados.eventos.filter((_, idx) => idx < 100).map(it => ({ key: it.id, text: `${it.id} - ${it.descricao}`, value: it.id })))
            else
                setFuncionariosFilter([])
        }   
    }

    function handleEventsBlur(_, { name }) {
        if (name === 'inputs')
            setEventsOptions(dados.eventos.filter((_, idx) => idx < 100).map(it => ({ key: it.id, text: `${it.id} - ${it.descricao}`, value: it.id })))
        else
            setFuncionariosFilter([])
    }

    const inputFormProps = { inputs, setInputs, eventos: dados.eventos, form, setForm, handleChange }

    return (
        <div className="row flex-grow-1 mt-2">
            <div className="col d-flex flex-column">
                <div className="row mx-0">
                    <div className="col px-0">
                        <Header as='h3'>
                            <Icon.Group size='big'>
                                <Icon name='keyboard' className='primary-color' />
                            </Icon.Group>
                            <Header.Content className='primary-color'>
                                Departamento Pessoal <Header.Subheader className='secondary-color'>Digitar Eventos</Header.Subheader>
                            </Header.Content>
                        </Header>
                    </div>
                    <div className='col-12 col-md-2 d-print-none px-0'>
                        <Button icon size='tiny' className='btn-red' floated='right' onClick={() => history.goBack()}>
                            <Icon name='arrow left' /> Voltar
                        </Button>
                    </div>
                </div>

                <Divider className='mt-2 custom-divider darken' fitted />

                <div className="row mt-2">
                    <div className='col'>
                        <Form size='tiny'>
                            <Form.Group>
                                <Form.Select
                                    width='2'
                                    required
                                    options={periodoOptions}
                                    label='Período'
                                    search
                                    selectOnBlur={false}
                                    placeholder='Selecionar Período'
                                    name='periodo'
                                    onChange={handleChange}
                                />

                                <Form.Select
                                    width='4'
                                    disabled={loading || form.periodo === ''}
                                    search
                                    noResultsMessage='Nenhum resultado encontrado'
                                    required
                                    label='Empresa'
                                    placeholder={loading === true ? 'Carregando...' : 'Selecionar Empresa'}
                                    name='empresa'
                                    selectOnBlur={false}
                                    value={form.empresa}
                                    onChange={handleChange}
                                    options={dados.empresas.map(x => ({ key: x.id_dpnet, text: x.id + ' - ' + x.nome, value: x.id }))}
                                />
                                <Form.Select
                                    width='5'
                                    disabled={loading || form.empresa === ''}
                                    search
                                    noResultsMessage='Nenhum resultado encontrado'
                                    required
                                    label='Funcionário'
                                    placeholder={loading === true ? 'Carregando...' : 'Selecionar Funcionário'}
                                    name='funcionario'
                                    selectOnBlur={false}
                                    onSearchChange={searchEvents}
                                    value={form.funcionario}
                                    onChange={handleChange}
                                    onBlur={handleEventsBlur}
                                    options={funcionariosFilter.length ? funcionariosFilter : form.empresa ? dados.funcionarios.filter(x => x.id_empresa === form.empresa).map(x => ({ key: x.id, text: `${x.codigo} - ${x.nome} / ${x.cargo}`, value: x.id })) : [{ text: '', value: '' }]}
                                />
                                <Form.Select
                                width='5'
                                    disabled={form.periodo === '' || form.empresa === '' || form.funcionario === '' ? true : false}
                                    search
                                    noResultsMessage='Nenhum resultado encontrado'
                                    label='Eventos'
                                    placeholder={loading === true ? 'Carregando...' : 'Selecionar Evento'}
                                    name='inputs'
                                    selectOnBlur={false}
                                    onSearchChange={searchEvents}
                                    onChange={handleChange}
                                    onBlur={handleEventsBlur}
                                    options={eventsOptions}
                                />
                            </Form.Group>
                        </Form>
                    </div>
                </div>

                <Divider className='mt-2 custom-divider darken' fitted />

                <div className='row mt-2'>
                    <div className="col">
                        {inputs.length > 0 && <InputsForm {...inputFormProps} />}
                    </div>
                </div>

            </div>
        </div>
    )
}
